import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api-client'

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string; icon_color?: string; priority?: string; password?: string }) =>
      api.createProject(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }) },
  })
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }) },
  })
}

export function useUpdateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('@/types').Project> }) =>
      api.updateProject(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }) },
  })
}

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { project_id: string; parent_folder_id?: string | null; name: string }) =>
      api.createFolder(data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['folders', variables.project_id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; projectId: string }) =>
      api.deleteFolder(vars.id),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['folders', vars.projectId] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useCreateThread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { project_id: string; folder_id?: string | null; title: string; description?: string; default_provider?: string; default_model?: string; language?: string }) =>
      api.createThread(data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['threads', variables.project_id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useDeleteThread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; projectId: string }) =>
      api.deleteThread(vars.id),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['threads', vars.projectId] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useArchiveThread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; projectId: string }) =>
      api.archiveThread(vars.id),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['threads', vars.projectId] })
      qc.invalidateQueries({ queryKey: ['folders', vars.projectId] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useSendMessage(threadId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { content: string; provider?: string; model?: string }) =>
      api.sendMessage(threadId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['thread', threadId] })
      qc.invalidateQueries({ queryKey: ['activity'] })
    },
  })
}

export function useGenerateSummary(threadId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data?: { provider?: string; model?: string }) =>
      api.generateSummary(threadId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['thread', threadId] })
    },
  })
}

export function useUpdateProvider() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ providerType, data }: { providerType: string; data: { api_key?: string; is_active?: boolean } }) =>
      api.updateProvider(providerType, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['providers'] })
    },
  })
}

export function useMoveFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; projectId: string; parent_folder_id: string | null }) =>
      api.updateFolder(vars.id, { parent_folder_id: vars.parent_folder_id } as any),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['folders', vars.projectId] })
    },
  })
}

export function useMoveThread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; projectId: string; folder_id: string | null }) =>
      api.updateThread(vars.id, { folder_id: vars.folder_id } as any),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['threads', vars.projectId] })
    },
  })
}

export function useUpdateMyKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { providerType: string; api_key: string }) =>
      api.updateMyKey(vars.providerType, { api_key: vars.api_key }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['providers'] })
    },
  })
}

export function useCreateDecision() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { project_id: string; thread_id?: string; title: string; content: string; type?: string }) =>
      api.createDecision(data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['decisions', variables.project_id] })
    },
  })
}

export function useCreateAppProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string; prompt?: string }) =>
      api.createAppProject(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['app-projects'] }) },
  })
}

export function useDeleteAppProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deleteAppProject(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['app-projects'] }) },
  })
}

export function useSaveAppFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { projectId: string; path: string; content: string; language?: string }) =>
      api.saveAppFile(vars.projectId, { path: vars.path, content: vars.content, language: vars.language }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['app-project', vars.projectId] })
    },
  })
}

export function useDeleteAppFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { projectId: string; fileId: string }) =>
      api.deleteAppFile(vars.projectId, vars.fileId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['app-project', vars.projectId] })
    },
  })
}

export function useGenerateAppCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { projectId: string; prompt: string; provider?: string; model?: string }) =>
      api.generateAppCode(vars.projectId, { prompt: vars.prompt, provider: vars.provider, model: vars.model }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['app-project', vars.projectId] })
    },
  })
}

export function useResolveAppError() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { projectId: string; errorId: string }) =>
      api.resolveAppError(vars.projectId, vars.errorId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['app-project', vars.projectId] })
    },
  })
}

export function useClearAppErrors() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => api.clearAppErrors(projectId),
    onSuccess: (_data, projectId) => {
      qc.invalidateQueries({ queryKey: ['app-project', projectId] })
    },
  })
}
